"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEITOS_QTY_FORMAT_EXAMPLE = void 0;
exports.parseLeitosQtyExpr = parseLeitosQtyExpr;
exports.leitosQtyAstToEsQuery = leitosQtyAstToEsQuery;
exports.LEITOS_QTY_FORMAT_EXAMPLE = 'Use por exemplo: ">=5 e <50" ou "(>3 e <=10) ou (>=500)"';
const OP_ALIASES = [
    [/^maior\s+(?:ou\s+)?igual(?:\s+a)?\b/i, '>='],
    [/^menor\s+(?:ou\s+)?igual(?:\s+a)?\b/i, '<='],
    [/^maior(?:\s+que|\s+do\s+que)?\b/i, '>'],
    [/^menor(?:\s+que|\s+do\s+que)?\b/i, '<'],
    [/^igual(?:\s+a)?\b/i, '='],
    [/^diferente(?:\s+de)?\b/i, '=']
];
const AND_RE = /^(?:e|and)\b/i;
const OR_RE = /^(?:ou|or)\b/i;
function tokenize(input) {
    const tokens = [];
    let i = 0;
    const src = input.trim();
    if (!src)
        return { error: 'Expressão vazia.' };
    while (i < src.length) {
        const ch = src[i];
        if (ch === ' ' || ch === '\t' || ch === '\n') {
            i++;
            continue;
        }
        if (ch === '(') {
            tokens.push({ type: 'lparen' });
            i++;
            continue;
        }
        if (ch === ')') {
            tokens.push({ type: 'rparen' });
            i++;
            continue;
        }
        if (ch === '>' || ch === '<') {
            if (src[i + 1] === '=') {
                tokens.push({ type: 'op', value: (ch + '=') });
                i += 2;
            }
            else {
                tokens.push({ type: 'op', value: ch });
                i++;
            }
            continue;
        }
        if (ch === '=') {
            tokens.push({ type: 'op', value: '=' });
            i++;
            continue;
        }
        if (ch >= '0' && ch <= '9') {
            let j = i + 1;
            while (j < src.length && src[j] >= '0' && src[j] <= '9')
                j++;
            const num = Number(src.slice(i, j));
            if (!Number.isFinite(num)) {
                return { error: `Número inválido próximo a "${src.slice(i, j)}".` };
            }
            tokens.push({ type: 'num', value: num });
            i = j;
            continue;
        }
        const rest = src.slice(i);
        const andMatch = AND_RE.exec(rest);
        if (andMatch) {
            tokens.push({ type: 'and' });
            i += andMatch[0].length;
            continue;
        }
        const orMatch = OR_RE.exec(rest);
        if (orMatch) {
            tokens.push({ type: 'or' });
            i += orMatch[0].length;
            continue;
        }
        let matched = false;
        for (const [re, op] of OP_ALIASES) {
            const m = re.exec(rest);
            if (m) {
                tokens.push({ type: 'op', value: op });
                i += m[0].length;
                matched = true;
                break;
            }
        }
        if (matched)
            continue;
        return { error: `Token inesperado próximo a "${rest.slice(0, 16)}".` };
    }
    return tokens;
}
function parseTokens(tokens) {
    let pos = 0;
    function peek() {
        return tokens[pos];
    }
    function consume() {
        return tokens[pos++];
    }
    function parseOr() {
        const first = parseAnd();
        if ('error' in first)
            return first;
        const items = [first];
        while (peek()?.type === 'or') {
            consume();
            const next = parseAnd();
            if ('error' in next)
                return next;
            items.push(next);
        }
        return items.length === 1 ? items[0] : { kind: 'or', items };
    }
    function parseAnd() {
        const first = parseAtom();
        if ('error' in first)
            return first;
        const items = [first];
        while (peek()?.type === 'and') {
            consume();
            const next = parseAtom();
            if ('error' in next)
                return next;
            items.push(next);
        }
        return items.length === 1 ? items[0] : { kind: 'and', items };
    }
    function parseAtom() {
        const tok = peek();
        if (!tok)
            return { error: 'Expressão incompleta.' };
        if (tok.type === 'lparen') {
            consume();
            const inner = parseOr();
            if ('error' in inner)
                return inner;
            const close = consume();
            if (!close || close.type !== 'rparen') {
                return { error: 'Parêntese de fechamento ausente.' };
            }
            return inner;
        }
        if (tok.type === 'op') {
            consume();
            const numTok = consume();
            if (!numTok || numTok.type !== 'num') {
                return { error: `Esperado número após operador "${tok.value}".` };
            }
            return { kind: 'cmp', op: tok.value, value: numTok.value };
        }
        if (tok.type === 'num') {
            consume();
            return { kind: 'cmp', op: '=', value: tok.value };
        }
        return { error: `Token inesperado: ${describeToken(tok)}.` };
    }
    const root = parseOr();
    if ('error' in root)
        return root;
    if (pos < tokens.length) {
        return { error: `Sobra de tokens após análise (a partir de ${describeToken(tokens[pos])}).` };
    }
    return root;
}
function describeToken(tok) {
    if (!tok)
        return 'fim da expressão';
    switch (tok.type) {
        case 'op':
            return `"${tok.value}"`;
        case 'num':
            return `número ${tok.value}`;
        case 'lparen':
            return '"("';
        case 'rparen':
            return '")"';
        case 'and':
            return '"e"';
        case 'or':
            return '"ou"';
    }
}
function normalize(node) {
    switch (node.kind) {
        case 'cmp':
            return `${node.op}${node.value}`;
        case 'and':
            return node.items.map(wrapIfNeeded).join(' e ');
        case 'or':
            return node.items.map(wrapIfNeeded).join(' ou ');
    }
}
function wrapIfNeeded(node) {
    if (node.kind === 'cmp')
        return normalize(node);
    return `(${normalize(node)})`;
}
function parseLeitosQtyExpr(input) {
    const tokensOrError = tokenize(input);
    if (!Array.isArray(tokensOrError)) {
        return { ok: false, message: tokensOrError.error };
    }
    const ast = parseTokens(tokensOrError);
    if ('error' in ast) {
        return { ok: false, message: ast.error };
    }
    const node = ast;
    return { ok: true, ast: node, normalized: normalize(node) };
}
function leitosQtyAstToEsQuery(node, field) {
    switch (node.kind) {
        case 'cmp': {
            const r = {};
            switch (node.op) {
                case '>':
                    r.gt = node.value;
                    break;
                case '>=':
                    r.gte = node.value;
                    break;
                case '<':
                    r.lt = node.value;
                    break;
                case '<=':
                    r.lte = node.value;
                    break;
                case '=':
                    r.gte = node.value;
                    r.lte = node.value;
                    break;
            }
            return { range: { [field]: r } };
        }
        case 'and':
            return {
                bool: {
                    must: node.items.map((it) => leitosQtyAstToEsQuery(it, field))
                }
            };
        case 'or':
            return {
                bool: {
                    should: node.items.map((it) => leitosQtyAstToEsQuery(it, field)),
                    minimum_should_match: 1
                }
            };
    }
}
//# sourceMappingURL=leitos-qty-expr.js.map